import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Plus, Search, Download, Edit, Trash2, ChevronLeft, ChevronRight, Upload, UserX, MoreVertical, UserPlus, Crown, Link, X, Copy, MapPin, Camera } from 'lucide-react';

// Custom hook for debounced value
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}

const RELATIONSHIPS = ['HOF', 'Wife', 'Husband', 'Son', 'Daughter', 'Father', 'Mother', 'Brother', 'Sister', 'Family Member', 'Other'];
const GENDERS = ['Male', 'Female'];
const BLOOD_GROUPS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-', 'Unknown'];
const MARITAL_STATUSES = ['Single', 'Married', 'Widowed', 'Divorced'];
const MISAQ_STATUSES = ['Done', 'Not Done'];
const OCCUPATIONS = ['Business', 'Service', 'Student', 'Housewife', 'Retired', 'Other'];
const SUB_OCCUPATIONS = {
  'Business': ['Retail', 'Wholesale', 'Manufacturing', 'Trading', 'Real Estate', 'Other'],
  'Service': ['Government', 'Private', 'IT/Software', 'Banking', 'Healthcare', 'Education', 'Other'],
  'Student': ['School', 'College', 'University', 'Professional Course', 'Other'],
  'Housewife': [],
  'Retired': ['Govt Pensioner', 'Private Pensioner', 'Other'],
  'Other': ['Self Employed', 'Freelancer', 'Unemployed', 'Other']
};
const REMOVAL_REASONS = ['Deceased', 'Gone / Shifted Permanently', 'Transferred to Another Jamaat', 'Duplicate Entry', 'Other'];

// Helper function to copy text to clipboard with fallback
const copyToClipboard = async (text, onSuccess, onError) => {
  try {
    // Try modern Clipboard API first
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      onSuccess && onSuccess();
      return;
    }
  } catch (err) {
    // Clipboard API failed, try fallback
  }
  
  // Fallback: Create temporary textarea element
  try {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    const success = document.execCommand('copy');
    document.body.removeChild(textArea);
    if (success) {
      onSuccess && onSuccess();
    } else {
      onError && onError();
    }
  } catch (err) {
    onError && onError();
  }
};

export default function MembersPage() {
  const { api, isAdmin } = useAuth();
  const [members, setMembers] = useState([]);
  const [families, setFamilies] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Read URL query params for dashboard card navigation
  const [searchParams, setSearchParams] = useState(() => new URLSearchParams(window.location.search));
  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '');
  const search = useDebounce(searchInput, 300);
  const [genderFilter, setGenderFilter] = useState(searchParams.get('gender') || '');
  const [bloodGroupFilter, setBloodGroupFilter] = useState(searchParams.get('blood_group') || '');
  const [misaqFilter, setMisaqFilter] = useState(searchParams.get('misaq') || '');
  const [areaFilter, setAreaFilter] = useState(searchParams.get('area') || '');
  const [areas, setAreas] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [editMember, setEditMember] = useState(null);
  const [removeMember, setRemoveMember] = useState(null);
  const [tagToHofMember, setTagToHofMember] = useState(null);
  const [makeHofMember, setMakeHofMember] = useState(null);
  const [assignMohallaMember, setAssignMohallaMember] = useState(null);

  const limit = 25;

  const fetchMembers = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page, limit });
      if (search) params.append('search', search);
      if (genderFilter) params.append('gender', genderFilter);
      if (bloodGroupFilter) params.append('blood_group', bloodGroupFilter);
      if (misaqFilter) params.append('misaq_status', misaqFilter);
      if (areaFilter) params.append('area', areaFilter);
      
      const response = await api.get(`/members?${params}`);
      setMembers(response.data.members);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Failed to fetch members');
    } finally {
      setLoading(false);
    }
  }, [api, page, search, genderFilter, bloodGroupFilter, misaqFilter, areaFilter]);

  const fetchFamilies = useCallback(async () => {
    try {
      const response = await api.get('/families?limit=1000');
      setFamilies(response.data.families);
    } catch (error) {
      console.error('Failed to fetch families');
    }
  }, [api]);

  // Fetch members when filters change
  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  // Fetch families once on mount
  useEffect(() => {
    fetchFamilies();
    api.get('/areas').then(r => setAreas(r.data.areas || [])).catch(() => {});
  }, []);

  const handleAddMember = async (data) => {
    try {
      await api.post('/members', data);
      toast.success('Member added successfully');
      setIsAddOpen(false);
      fetchMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add member');
    }
  };

  const handleUpdateMember = async (memberId, data) => {
    try {
      await api.put(`/members/${memberId}`, data);
      toast.success('Member updated successfully');
      setEditMember(null);
      fetchMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update member');
    }
  };

  const handleDeleteMember = async (memberId) => {
    if (!window.confirm('Are you sure you want to delete this member?')) return;
    try {
      await api.delete(`/members/${memberId}`);
      toast.success('Member deleted successfully');
      fetchMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete member');
    }
  };

  const handleExport = async () => {
    try {
      const response = await api.get('/download/members', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Members.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Export started');
    } catch (error) {
      toast.error('Failed to export');
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="members-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-semibold text-slate-800">Members</h1>
          <p className="text-sm text-slate-500">Manage all Jamaat members</p>
        </div>
        
        {isAdmin && (
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleExport} data-testid="export-members-btn" className="text-xs sm:text-sm">
              <Download className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Export</span>
            </Button>
            <MemberUploadDialog api={api} onSuccess={fetchMembers} />
            <PhotoUploadDialog api={api} onSuccess={fetchMembers} />
            <UpdateMemberDataDialog api={api} onSuccess={fetchMembers} />
            <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
              <DialogTrigger asChild>
                <Button className="bg-navy hover:bg-navy-light text-xs sm:text-sm" size="sm" data-testid="add-member-btn">
                  <Plus className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Add Member</span>
                  <span className="sm:hidden">Add</span>
                </Button>
              </DialogTrigger>
              <MemberFormDialog 
                families={families}
                onSubmit={handleAddMember} 
                onClose={() => setIsAddOpen(false)}
              />
            </Dialog>
          </div>
        )}
      </div>

      {/* Filters */}
      <Card className="border-0 shadow-card">
        <CardContent className="p-3 sm:p-4">
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-6 gap-2 sm:gap-4">
            <div className="relative col-span-2 lg:col-span-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search name, mobile..."
                value={searchInput}
                onChange={(e) => { setSearchInput(e.target.value); setPage(1); }}
                className="pl-9 text-sm"
                data-testid="member-search-input"
              />
            </div>
            <Select value={areaFilter || 'all'} onValueChange={(v) => { setAreaFilter(v === 'all' ? '' : v); setPage(1); }}>
              <SelectTrigger data-testid="area-filter" className="text-xs sm:text-sm">
                <SelectValue placeholder="Area" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Areas</SelectItem>
                {areas.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={genderFilter} onValueChange={(v) => { setGenderFilter(v === 'all' ? '' : v); setPage(1); }}>
              <SelectTrigger data-testid="gender-filter" className="text-xs sm:text-sm">
                <SelectValue placeholder="Gender" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Genders</SelectItem>
                {GENDERS.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={bloodGroupFilter} onValueChange={(v) => { setBloodGroupFilter(v === 'all' ? '' : v); setPage(1); }}>
              <SelectTrigger data-testid="blood-group-filter" className="text-xs sm:text-sm">
                <SelectValue placeholder="Blood" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Blood</SelectItem>
                {BLOOD_GROUPS.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={misaqFilter} onValueChange={(v) => { setMisaqFilter(v === 'all' ? '' : v); setPage(1); }} className="col-span-2 sm:col-span-1">
              <SelectTrigger data-testid="misaq-filter" className="text-xs sm:text-sm">
                <SelectValue placeholder="Misaq" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Misaq</SelectItem>
                {MISAQ_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-0 shadow-card overflow-hidden">
        <div className="overflow-x-auto">
          <Table className="data-table min-w-[800px] md:min-w-full">
            <TableHeader>
              <TableRow>
                <TableHead className="w-10"></TableHead>
                <TableHead className="whitespace-nowrap">ITS</TableHead>
                <TableHead className="whitespace-nowrap min-w-[120px]">Name</TableHead>
                <TableHead className="hidden md:table-cell whitespace-nowrap">HOF Name</TableHead>
                <TableHead className="whitespace-nowrap">Type</TableHead>
                <TableHead className="whitespace-nowrap">Mobile</TableHead>
                <TableHead className="hidden sm:table-cell whitespace-nowrap">Age</TableHead>
                <TableHead className="hidden lg:table-cell whitespace-nowrap">Gender</TableHead>
                <TableHead className="whitespace-nowrap">Misaq</TableHead>
                <TableHead className="hidden lg:table-cell whitespace-nowrap">Area</TableHead>
                <TableHead className="hidden lg:table-cell whitespace-nowrap">Mohalla</TableHead>
                {isAdmin && <TableHead className="text-right whitespace-nowrap">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={12} className="text-center py-8">
                    <div className="w-6 h-6 border-2 border-navy border-t-transparent rounded-full animate-spin mx-auto" />
                  </TableCell>
                </TableRow>
              ) : members.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={12} className="text-center py-8 text-slate-500">
                    No members found
                  </TableCell>
                </TableRow>
              ) : (
                members.map((member) => (
                  <TableRow key={member.id} data-testid={`member-row-${member.id}`}>
                    <TableCell className="p-2">
                      <MemberAvatar photoUrl={member.photo_url} name={member.full_name} size="sm" />
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {member.its_id ? (
                        <button
                          onClick={() => copyToClipboard(
                            member.its_id,
                            () => toast.success(`Copied: ${member.its_id}`),
                            () => toast.error('Failed to copy')
                          )}
                          className="flex items-center gap-1 hover:text-blue-600 hover:bg-blue-50 px-1 py-0.5 rounded cursor-pointer transition-colors"
                          title="Click to copy ITS"
                        >
                          {member.its_id}
                          <Copy className="w-3 h-3 opacity-50" />
                        </button>
                      ) : '-'}
                    </TableCell>
                    <TableCell className="font-medium text-sm">{member.full_name}</TableCell>
                    <TableCell className="hidden md:table-cell text-sm">{member.hof_name || '-'}</TableCell>
                    <TableCell>
                      {member.is_hof || member.relationship === 'Self' ? (
                        <Badge className="bg-navy text-white text-xs">HOF</Badge>
                      ) : (
                        <Badge variant="outline" className="text-slate-600 text-xs">FM</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">{member.mobile}</TableCell>
                    <TableCell className="hidden sm:table-cell text-sm">{member.age}</TableCell>
                    <TableCell className="hidden lg:table-cell text-sm">{member.gender}</TableCell>
                    <TableCell>
                      <Badge className={`text-xs ${member.misaq_status === 'Done' ? 'badge-success' : 'badge-warning'}`}>
                        {member.misaq_status}
                      </Badge>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell text-sm">{member.area || '-'}</TableCell>
                    <TableCell className="hidden lg:table-cell text-sm">{member.mohalla || '-'}</TableCell>
                    {isAdmin && (
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" data-testid={`member-actions-${member.id}`}>
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => setEditMember(member)}>
                              <Edit className="w-4 h-4 mr-2" />
                              Edit Member
                            </DropdownMenuItem>
                            {member.is_hof && (!member.mohalla || member.mohalla === '-' || member.mohalla === '') && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => setAssignMohallaMember(member)}>
                                  <MapPin className="w-4 h-4 mr-2" />
                                  Assign Mohalla/Sector
                                </DropdownMenuItem>
                              </>
                            )}
                            {/* For HOF members - option to convert to family member */}
                            {member.is_hof && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => setTagToHofMember(member)}>
                                  <Link className="w-4 h-4 mr-2" />
                                  Move to Different Family
                                </DropdownMenuItem>
                              </>
                            )}
                            {/* For non-HOF members OR untagged members (no hof_id) */}
                            {(!member.is_hof && member.relationship !== 'Self') || (!member.hof_id || !member.hof_name) ? (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => setTagToHofMember(member)}>
                                  <Link className="w-4 h-4 mr-2" />
                                  {member.hof_id && member.hof_name ? 'Tag to Different HOF' : 'Tag to HOF / Assign Family'}
                                </DropdownMenuItem>
                                {!member.is_hof && (
                                  <DropdownMenuItem onClick={() => setMakeHofMember(member)}>
                                    <Crown className="w-4 h-4 mr-2" />
                                    Make HOF (New Family)
                                  </DropdownMenuItem>
                                )}
                              </>
                            ) : null}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              onClick={() => setRemoveMember(member)}
                              className="text-red-600"
                            >
                              <UserX className="w-4 h-4 mr-2" />
                              Remove Member
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
        
        {/* Pagination */}
        <div className="flex flex-col sm:flex-row items-center justify-between p-3 sm:p-4 border-t gap-2">
          <p className="text-xs sm:text-sm text-slate-500 text-center sm:text-left">
            Showing {((page - 1) * limit) + 1}-{Math.min(page * limit, total)} of {total}
          </p>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              data-testid="members-prev-page"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-xs sm:text-sm text-slate-600">{page}/{totalPages || 1}</span>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              data-testid="members-next-page"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </Card>

      {/* Edit Dialog */}
      {editMember && (
        <Dialog open={!!editMember} onOpenChange={() => setEditMember(null)}>
          <MemberFormDialog 
            member={editMember}
            families={families}
            onSubmit={(data) => handleUpdateMember(editMember.id, data)} 
            onClose={() => setEditMember(null)}
          />
        </Dialog>
      )}

      {/* Remove Member Dialog */}
      {removeMember && (
        <RemoveMemberDialog
          member={removeMember}
          api={api}
          onClose={() => setRemoveMember(null)}
          onSuccess={() => { fetchMembers(); setRemoveMember(null); }}
        />
      )}

      {/* Tag to HOF Dialog */}
      {tagToHofMember && (
        <TagToHofDialog
          member={tagToHofMember}
          api={api}
          onClose={() => setTagToHofMember(null)}
          onSuccess={() => { fetchMembers(); fetchFamilies(); setTagToHofMember(null); }}
        />
      )}

      {/* Make HOF Dialog */}
      {makeHofMember && (
        <MakeHofDialog
          member={makeHofMember}
          api={api}
          onClose={() => setMakeHofMember(null)}
          onSuccess={() => { fetchMembers(); fetchFamilies(); setMakeHofMember(null); }}
        />
      )}

      {/* Assign Mohalla/Sector Dialog */}
      {assignMohallaMember && (
        <AssignMohallaDialog
          member={assignMohallaMember}
          api={api}
          onClose={() => setAssignMohallaMember(null)}
          onSuccess={() => { fetchMembers(); fetchFamilies(); setAssignMohallaMember(null); }}
        />
      )}
    </div>
  );
}

function RemoveMemberDialog({ member, api, onClose, onSuccess }) {
  const [reason, setReason] = useState('');
  const [remarks, setRemarks] = useState('');
  const [loading, setLoading] = useState(false);

  const handleRemove = async () => {
    if (!reason) {
      toast.error('Please select a reason');
      return;
    }
    if (reason === 'Other' && !remarks.trim()) {
      toast.error('Please provide remarks for "Other" reason');
      return;
    }

    setLoading(true);
    try {
      await api.post(`/members/${member.id}/remove`, { reason, remarks: remarks.trim() });
      toast.success('Member removed successfully');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove member');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="text-red-600">Remove Member</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-800">
              You are about to remove: <span className="font-semibold">{member.full_name}</span>
            </p>
            {member.hof_name && (
              <p className="text-xs text-red-600 mt-1">Family: {member.hof_name}</p>
            )}
          </div>

          <div>
            <Label className="form-label">Reason for Removal *</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger>
                <SelectValue placeholder="Select reason" />
              </SelectTrigger>
              <SelectContent>
                {REMOVAL_REASONS.map(r => (
                  <SelectItem key={r} value={r}>{r}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="form-label">Remarks {reason === 'Other' && '*'}</Label>
            <Textarea
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="Additional details..."
              rows={3}
            />
          </div>

          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
            <p className="font-medium">Note:</p>
            <ul className="list-disc list-inside mt-1 text-xs">
              <li>Member will be marked as inactive</li>
              <li>Removed from Family, FMB, Distribution & Messaging</li>
              <li>Will NOT appear in Non-Tagged list</li>
              <li>This action is logged for audit</li>
            </ul>
          </div>
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
          <Button 
            onClick={handleRemove} 
            disabled={loading || !reason}
            className="bg-red-600 hover:bg-red-700"
          >
            {loading ? 'Removing...' : 'Remove Member'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MemberFormDialog({ member, families, onSubmit, onClose }) {
  const [formData, setFormData] = useState({
    its_id: member?.its_id || '',
    hof_id: member?.hof_id || '',
    full_name: member?.full_name || '',
    relationship: member?.relationship || '',
    mobile: member?.mobile || '',
    age: member?.age || 0,
    gender: member?.gender || 'Male',
    blood_group: member?.blood_group || '',
    marital_status: member?.marital_status || 'Single',
    misaq_status: member?.misaq_status || 'Not Done',
    occupation: member?.occupation || '',
    sub_occupation: member?.sub_occupation || ''
  });
  const [loading, setLoading] = useState(false);
  const [familySearch, setFamilySearch] = useState('');
  const [showFamilyDropdown, setShowFamilyDropdown] = useState(false);
  const familyDropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (familyDropdownRef.current && !familyDropdownRef.current.contains(event.target)) {
        setShowFamilyDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter families by ITS or name
  const filteredFamilies = families.filter(f => {
    if (!familySearch) return true;
    const search = familySearch.toLowerCase();
    const hofName = (f.hof_name || '').toLowerCase();
    const hofId = (f.id || '').toLowerCase();
    const hofIts = (f.hof_its_id || '').toLowerCase();
    return hofName.includes(search) || hofId.includes(search) || hofIts.includes(search);
  }).slice(0, 50); // Limit to 50 results for performance

  const selectedFamily = families.find(f => f.id === formData.hof_id);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    await onSubmit(formData);
    setLoading(false);
  };

  return (
    <DialogContent className="max-w-lg">
      <DialogHeader>
        <DialogTitle>{member ? 'Edit Member' : 'Add New Member'}</DialogTitle>
      </DialogHeader>
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* ITS Number - Primary field highlighted */}
        <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
          <Label className="form-label text-emerald-700 font-semibold">ITS Number *</Label>
          <Input 
            value={formData.its_id} 
            onChange={(e) => setFormData({...formData, its_id: e.target.value})}
            placeholder="Enter 8-digit ITS Number"
            className="mt-1 border-emerald-300 focus:border-emerald-500 text-lg font-mono"
            required
            data-testid="member-its-input"
          />
          <p className="text-xs text-emerald-600 mt-1">ITS Number is the primary identifier</p>
        </div>
        {!member && (
          <div>
            <Label className="form-label">Family (HOF) *</Label>
            <div className="relative" ref={familyDropdownRef}>
              <div 
                className="flex items-center border rounded-md bg-white cursor-pointer"
                onClick={() => setShowFamilyDropdown(!showFamilyDropdown)}
              >
                <Search className="w-4 h-4 ml-3 text-slate-400" />
                <Input
                  value={familySearch}
                  onChange={(e) => {
                    setFamilySearch(e.target.value);
                    setShowFamilyDropdown(true);
                  }}
                  placeholder={selectedFamily ? `${selectedFamily.hof_name} (${selectedFamily.hof_its_id || selectedFamily.id})` : "Search by ITS or name..."}
                  className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowFamilyDropdown(true);
                  }}
                  data-testid="hof-search-input"
                />
                {formData.hof_id && (
                  <button
                    type="button"
                    className="p-2 hover:text-red-500"
                    onClick={(e) => {
                      e.stopPropagation();
                      setFormData({...formData, hof_id: ''});
                      setFamilySearch('');
                    }}
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              {showFamilyDropdown && (
                <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-y-auto">
                  {filteredFamilies.length === 0 ? (
                    <div className="p-3 text-sm text-slate-500 text-center">
                      {familySearch ? 'No families found' : 'Type to search...'}
                    </div>
                  ) : (
                    filteredFamilies.map(f => (
                      <div
                        key={f.id}
                        className={`p-2 cursor-pointer hover:bg-slate-100 ${formData.hof_id === f.id ? 'bg-blue-50 border-l-2 border-blue-500' : ''}`}
                        onClick={() => {
                          setFormData({...formData, hof_id: f.id});
                          setFamilySearch('');
                          setShowFamilyDropdown(false);
                        }}
                      >
                        <div className="font-medium text-sm">{f.hof_name}</div>
                        <div className="text-xs text-slate-500">
                          ITS: <span className="font-mono">{f.hof_its_id || f.id.replace('HOF-', '')}</span>
                          {f.mohalla && <span className="ml-2">• {f.mohalla}</span>}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
            {selectedFamily && (
              <p className="text-xs text-green-600 mt-1">
                Selected: {selectedFamily.hof_name} ({selectedFamily.mohalla || 'No Mohalla'})
              </p>
            )}
          </div>
        )}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="form-label">Full Name *</Label>
            <Input 
              value={formData.full_name} 
              onChange={(e) => setFormData({...formData, full_name: e.target.value})}
              required
              data-testid="member-name-input"
            />
          </div>
          <div>
            <Label className="form-label">Relationship *</Label>
            <Select value={formData.relationship} onValueChange={(v) => setFormData({...formData, relationship: v})}>
              <SelectTrigger data-testid="relationship-select">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                {RELATIONSHIPS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="form-label">Mobile</Label>
            <Input 
              value={formData.mobile} 
              onChange={(e) => setFormData({...formData, mobile: e.target.value})}
              data-testid="member-mobile-input"
            />
          </div>
          <div>
            <Label className="form-label">Age</Label>
            <Input 
              type="number"
              value={formData.age} 
              onChange={(e) => setFormData({...formData, age: parseInt(e.target.value) || 0})}
              data-testid="member-age-input"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="form-label">Gender</Label>
            <Select value={formData.gender} onValueChange={(v) => setFormData({...formData, gender: v})}>
              <SelectTrigger data-testid="member-gender-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {GENDERS.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="form-label">Blood Group</Label>
            <Select value={formData.blood_group} onValueChange={(v) => setFormData({...formData, blood_group: v})}>
              <SelectTrigger data-testid="member-blood-select">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                {BLOOD_GROUPS.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="form-label">Marital Status</Label>
            <Select value={formData.marital_status} onValueChange={(v) => setFormData({...formData, marital_status: v})}>
              <SelectTrigger data-testid="member-marital-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MARITAL_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="form-label">Misaq Status</Label>
            <Select value={formData.misaq_status} onValueChange={(v) => setFormData({...formData, misaq_status: v})}>
              <SelectTrigger data-testid="member-misaq-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MISAQ_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        {/* Occupation Fields */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="form-label">Occupation</Label>
            <Select 
              value={formData.occupation || 'not_specified'} 
              onValueChange={(v) => setFormData({...formData, occupation: v === 'not_specified' ? '' : v, sub_occupation: ''})}
            >
              <SelectTrigger data-testid="member-occupation-select">
                <SelectValue placeholder="Select Occupation" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="not_specified">Not Specified</SelectItem>
                {OCCUPATIONS.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="form-label">Sub-Occupation</Label>
            <Select 
              value={formData.sub_occupation || 'not_specified'} 
              onValueChange={(v) => setFormData({...formData, sub_occupation: v === 'not_specified' ? '' : v})}
              disabled={!formData.occupation || !SUB_OCCUPATIONS[formData.occupation]?.length}
            >
              <SelectTrigger data-testid="member-sub-occupation-select">
                <SelectValue placeholder={formData.occupation ? "Select Sub-Occupation" : "Select Occupation first"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="not_specified">Not Specified</SelectItem>
                {(SUB_OCCUPATIONS[formData.occupation] || []).map(s => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline">Cancel</Button>
          </DialogClose>
          <Button type="submit" className="bg-navy hover:bg-navy-light" disabled={loading} data-testid="save-member-btn">
            {loading ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

function MemberUploadDialog({ api, onSuccess }) {
  const [isOpen, setIsOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleUpload = async () => {
    if (!file) return;
    
    setLoading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await api.post('/upload/members', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`Uploaded ${response.data.members_created} members`);
      if (response.data.errors?.length > 0) {
        toast.warning(`${response.data.errors.length} errors occurred`);
      }
      setIsOpen(false);
      setFile(null);
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="upload-members-btn">
          <Upload className="w-4 h-4 mr-2" />
          Upload
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Upload Members Excel</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="border-2 border-dashed rounded-lg p-6 text-center">
            <Input 
              type="file" 
              accept=".xlsx,.xls" 
              onChange={(e) => setFile(e.target.files[0])}
              className="hidden"
              id="members-file-upload"
            />
            <label htmlFor="members-file-upload" className="cursor-pointer">
              <Upload className="w-8 h-8 mx-auto text-slate-400 mb-2" />
              <p className="text-sm text-slate-600">{file ? file.name : 'Click to select Excel file'}</p>
              <p className="text-xs text-slate-400 mt-1">Required columns: HOF ID, Full Name, Relationship</p>
            </label>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button onClick={handleUpload} disabled={!file || loading} className="bg-navy">
              {loading ? 'Uploading...' : 'Upload'}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Photo Enlargement Modal
function PhotoModal({ isOpen, onClose, photoUrl, name }) {
  const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
  const fullUrl = photoUrl?.startsWith('http') ? photoUrl : `${API_BASE}${photoUrl}`;
  
  if (!isOpen) return null;
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md p-2 bg-white">
        <div className="relative">
          <button 
            onClick={onClose}
            className="absolute -top-2 -right-2 z-10 bg-white rounded-full p-1 shadow-lg hover:bg-slate-100"
          >
            <X className="w-5 h-5" />
          </button>
          <img 
            src={fullUrl} 
            alt={name || 'Member Photo'}
            className="w-full h-auto max-h-[70vh] object-contain rounded-lg"
          />
          {name && (
            <p className="text-center text-sm font-medium text-slate-700 mt-2 pb-2">{name}</p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Member Avatar Component with fallback placeholder and click to enlarge
function MemberAvatar({ photoUrl, name, size = 'sm', className = '', clickable = true }) {
  const [hasError, setHasError] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
  
  const sizeClasses = {
    xs: 'w-6 h-6 text-xs',
    sm: 'w-8 h-8 text-xs',
    md: 'w-12 h-12 text-sm',
    lg: 'w-16 h-16 text-lg',
    xl: 'w-24 h-24 text-xl'
  };
  
  const initials = name ? name.split(' ').slice(0, 2).map(n => n[0]).join('').toUpperCase() : '?';
  
  const handleClick = (e) => {
    if (clickable && photoUrl && !hasError) {
      e.stopPropagation();
      setShowModal(true);
    }
  };
  
  if (photoUrl && !hasError) {
    const fullUrl = photoUrl.startsWith('http') ? photoUrl : `${API_BASE}${photoUrl}`;
    return (
      <>
        <img 
          src={fullUrl} 
          alt={name || 'Member'}
          onError={() => setHasError(true)}
          onClick={handleClick}
          className={`${sizeClasses[size]} rounded-full object-cover flex-shrink-0 ${className} ${clickable ? 'cursor-pointer hover:ring-2 hover:ring-blue-400 transition-all' : ''}`}
        />
        <PhotoModal 
          isOpen={showModal} 
          onClose={() => setShowModal(false)} 
          photoUrl={photoUrl} 
          name={name} 
        />
      </>
    );
  }
  
  // Fallback placeholder
  return (
    <div className={`${sizeClasses[size]} rounded-full bg-slate-200 flex items-center justify-center text-slate-500 font-medium flex-shrink-0 ${className}`}>
      {initials}
    </div>
  );
}

// Photo Upload Dialog Component
function PhotoUploadDialog({ api, onSuccess }) {
  const [isOpen, setIsOpen] = useState(false);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [overwriteDuplicates, setOverwriteDuplicates] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFiles = Array.from(e.dataTransfer.files).filter(
      f => f.type.startsWith('image/') && (f.name.endsWith('.jpg') || f.name.endsWith('.jpeg') || f.name.endsWith('.png'))
    );
    setFiles(prev => [...prev, ...droppedFiles]);
  };

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles(prev => [...prev, ...selectedFiles]);
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (files.length === 0) return;
    
    setLoading(true);
    setResults(null);
    
    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });
    
    try {
      const response = await api.post(`/members/photos/upload?overwrite_duplicates=${overwriteDuplicates}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setResults(response.data);
      
      const { summary } = response.data;
      if (summary.matched > 0) {
        toast.success(`${summary.matched} photos uploaded successfully`);
      }
      if (summary.not_found > 0) {
        toast.warning(`${summary.not_found} ITS numbers not found`);
      }
      if (summary.errors > 0) {
        toast.error(`${summary.errors} errors occurred`);
      }
      
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setFiles([]);
    setResults(null);
  };

  const exportErrors = async (logId) => {
    try {
      const response = await api.get(`/members/photos/logs/${logId}/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `photo_upload_log_${logId}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to export log');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => open ? setIsOpen(true) : handleClose()}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid="upload-photos-btn" className="text-xs sm:text-sm">
          <Upload className="w-4 h-4 sm:mr-2" />
          <span className="hidden sm:inline">Photos</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Bulk Upload Member Photos</DialogTitle>
        </DialogHeader>
        
        {!results ? (
          <div className="space-y-4">
            {/* Instructions */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
              <p className="font-medium text-blue-800 mb-1">File naming rule:</p>
              <p className="text-blue-700">Photos must be named with <span className="font-mono font-bold">ITS number</span> (e.g., 50462579.jpg)</p>
              <p className="text-blue-600 text-xs mt-1">Accepted formats: JPG, JPEG, PNG • Auto-compressed to &lt;500KB</p>
            </div>
            
            {/* Drag & Drop Zone */}
            <div 
              className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                isDragging ? 'border-blue-500 bg-blue-50' : 'border-slate-300'
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <input 
                type="file" 
                ref={fileInputRef}
                accept=".jpg,.jpeg,.png"
                multiple
                onChange={handleFileSelect}
                className="hidden"
                id="photo-upload"
              />
              <label htmlFor="photo-upload" className="cursor-pointer block">
                <Upload className="w-10 h-10 mx-auto text-slate-400 mb-2" />
                <p className="text-sm text-slate-600 font-medium">
                  {isDragging ? 'Drop photos here' : 'Drag & drop photos or click to browse'}
                </p>
              </label>
            </div>
            
            {/* Selected Files List */}
            {files.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium">{files.length} file(s) selected</p>
                  <Button variant="ghost" size="sm" onClick={() => setFiles([])}>Clear All</Button>
                </div>
                <div className="max-h-40 overflow-y-auto space-y-1 border rounded-lg p-2">
                  {files.map((file, idx) => (
                    <div key={idx} className="flex items-center justify-between text-sm bg-slate-50 rounded px-2 py-1">
                      <span className="font-mono truncate flex-1">{file.name}</span>
                      <span className="text-slate-500 text-xs mx-2">{(file.size / 1024).toFixed(0)} KB</span>
                      <button onClick={() => removeFile(idx)} className="text-red-500 hover:text-red-700">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Options */}
            <div className="flex items-center gap-2">
              <input 
                type="checkbox" 
                id="overwrite" 
                checked={overwriteDuplicates}
                onChange={(e) => setOverwriteDuplicates(e.target.checked)}
                className="rounded"
              />
              <label htmlFor="overwrite" className="text-sm text-slate-600">
                Overwrite existing photos if duplicate ITS found
              </label>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={handleClose}>Cancel</Button>
              <Button 
                onClick={handleUpload} 
                disabled={files.length === 0 || loading} 
                className="bg-navy"
              >
                {loading ? 'Processing...' : `Upload ${files.length} Photo${files.length !== 1 ? 's' : ''}`}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          /* Results View */
          <div className="space-y-4">
            {/* Summary Cards */}
            <div className="grid grid-cols-4 gap-2">
              <div className="bg-green-50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-green-600">{results.summary.matched}</p>
                <p className="text-xs text-green-700">Matched</p>
              </div>
              <div className="bg-orange-50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-orange-600">{results.summary.not_found}</p>
                <p className="text-xs text-orange-700">Not Found</p>
              </div>
              <div className="bg-red-50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-red-600">{results.summary.errors}</p>
                <p className="text-xs text-red-700">Errors</p>
              </div>
              <div className="bg-blue-50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-blue-600">{results.summary.duplicates_replaced}</p>
                <p className="text-xs text-blue-700">Replaced</p>
              </div>
            </div>
            
            {/* Results List */}
            <div className="max-h-60 overflow-y-auto border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ITS</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Size</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {results.results.map((result, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="font-mono text-xs">{result.its_id}</TableCell>
                      <TableCell className="text-sm">{result.member_name || '-'}</TableCell>
                      <TableCell>
                        <Badge className={`text-xs ${
                          result.status === 'success' ? 'bg-green-100 text-green-800' :
                          result.status === 'duplicate_replaced' ? 'bg-blue-100 text-blue-800' :
                          result.status === 'not_found' ? 'bg-orange-100 text-orange-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {result.status.replace('_', ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-slate-500">
                        {result.file_size ? `${(result.file_size / 1024).toFixed(0)} KB` : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => exportErrors(results.upload_log_id)}>
                <Download className="w-4 h-4 mr-2" />
                Export Report
              </Button>
              <Button onClick={handleClose} className="bg-navy">Done</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// New component for updating existing member data
function UpdateMemberDataDialog({ api, onSuccess }) {
  const [isOpen, setIsOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleUpload = async () => {
    if (!file) return;
    
    setLoading(true);
    setResult(null);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await api.post('/upload/update-members', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setResult(response.data);
      toast.success(`Updated ${response.data.members_updated} members`);
      if (response.data.members_not_found > 0) {
        toast.warning(`${response.data.members_not_found} members not found by ITS`);
      }
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Update failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { setIsOpen(open); if (!open) { setResult(null); setFile(null); } }}>
      <DialogTrigger asChild>
        <Button variant="outline" className="bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100">
          <Upload className="w-4 h-4 mr-2" />
          Update Data
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Update Existing Member Data</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-800">
            <p className="font-medium mb-1">Updates existing members by ITS (without changing Mohalla/Sector)</p>
            <p className="text-xs">Updates: Name, Mobile, Title, Organisation, Qualification, Email, WhatsApp, Blood Group, etc.</p>
            <p className="text-xs mt-1 text-blue-600">✨ New HOFs will be created as Untagged families (assign Mohalla later in FMB tab)</p>
          </div>
          
          <div className="border-2 border-dashed rounded-lg p-6 text-center">
            <Input 
              type="file" 
              accept=".xlsx,.xls" 
              onChange={(e) => { setFile(e.target.files[0]); setResult(null); }}
              className="hidden"
              id="update-data-file-upload"
            />
            <label htmlFor="update-data-file-upload" className="cursor-pointer">
              <Upload className="w-8 h-8 mx-auto text-slate-400 mb-2" />
              <p className="text-sm text-slate-600">{file ? file.name : 'Click to select Excel file'}</p>
              <p className="text-xs text-slate-400 mt-1">Required column: ITS_ID</p>
            </label>
          </div>
          
          {result && (
            <div className="p-3 bg-green-50 rounded-lg text-sm">
              <p className="font-medium text-green-800">✓ Updated {result.members_updated} members</p>
              {result.new_families_created > 0 && (
                <div className="mt-2 text-blue-700 bg-blue-50 p-2 rounded">
                  <p className="font-medium">🏠 {result.new_families_created} new families created (Untagged):</p>
                  <p className="text-xs mt-1">
                    {result.new_families?.map(f => `${f.name} (${f.its})`).join(', ')}
                  </p>
                  <p className="text-xs mt-1 text-blue-600">→ Go to FMB → Untagged tab to assign Mohalla/Sector</p>
                </div>
              )}
              {result.relationships_created > 0 && (
                <div className="mt-2 text-purple-700 bg-purple-50 p-2 rounded">
                  <p className="font-medium">🔗 {result.relationships_created} relationships created</p>
                  <p className="text-xs mt-1 text-purple-600">
                    Family relationships auto-linked based on Father/Mother/Spouse ITS IDs
                  </p>
                </div>
              )}
              {result.members_not_found > 0 && (
                <div className="mt-2 text-amber-700">
                  <p className="font-medium">⚠ {result.members_not_found} ITS not found (not HOF):</p>
                  <p className="text-xs mt-1 font-mono">
                    {result.not_found_its_ids?.slice(0, 10).join(', ')}
                    {result.not_found_its_ids?.length > 10 && '...'}
                  </p>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Close</Button>
            </DialogClose>
            <Button onClick={handleUpload} disabled={!file || loading} className="bg-blue-600 hover:bg-blue-700">
              {loading ? 'Updating...' : 'Update Data'}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TagToHofDialog({ member, api, onClose, onSuccess }) {
  const [createAsNewHof, setCreateAsNewHof] = useState(false);
  const [searchIts, setSearchIts] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedFamily, setSelectedFamily] = useState(null);
  const [selectedRelationship, setSelectedRelationship] = useState('Family Member');
  const [searching, setSearching] = useState(false);
  const [mohallas, setMohallas] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [formData, setFormData] = useState({
    mohalla: '',
    sector: '',
    thaali_number: '',
    address: '',
    flat_number: ''
  });
  const [loading, setLoading] = useState(false);

  // Check if member is currently an HOF
  const isCurrentlyHof = member.is_hof || member.relationship === 'Self';

  useEffect(() => {
    if (createAsNewHof) {
      fetchMohallas();
    }
  }, [createAsNewHof]);

  useEffect(() => {
    if (formData.mohalla) {
      fetchSectors(formData.mohalla);
    } else {
      setSectors([]);
    }
  }, [formData.mohalla]);

  const fetchMohallas = async () => {
    try {
      const response = await api.get('/mohallas');
      setMohallas(response.data.mohallas || []);
    } catch (error) {
      console.error('Failed to fetch mohallas');
    }
  };

  const fetchSectors = async (mohallaName) => {
    try {
      const response = await api.get(`/sectors?mohalla=${encodeURIComponent(mohallaName)}`);
      setSectors(response.data.sectors || []);
    } catch (error) {
      console.error('Failed to fetch sectors');
    }
  };

  // Search families by ITS number or HOF name
  const handleSearch = async () => {
    if (!searchIts.trim()) {
      toast.error('Please enter ITS number or HOF name to search');
      return;
    }
    
    setSearching(true);
    setSelectedFamily(null);
    try {
      const response = await api.get(`/families?search=${encodeURIComponent(searchIts.trim())}&limit=20`);
      const families = response.data.families || [];
      // Filter out current family
      const filtered = families.filter(f => f.id !== member.hof_id);
      setSearchResults(filtered);
      if (filtered.length === 0) {
        toast.info('No families found matching your search');
      }
    } catch (error) {
      toast.error('Failed to search families');
    } finally {
      setSearching(false);
    }
  };

  const handleTagToFamily = async () => {
    if (!selectedFamily) {
      toast.error('Please select a family');
      return;
    }

    setLoading(true);
    try {
      await api.put(`/members/${member.id}/tag-to-hof`, { 
        new_hof_id: selectedFamily.id,
        relationship: selectedRelationship 
      });
      toast.success(`Member ${isCurrentlyHof ? 'moved' : 'tagged'} to ${selectedFamily.hof_name}`);
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to tag member');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNewHof = async () => {
    if (!formData.mohalla || !formData.sector) {
      toast.error('Please select Mohalla and Sector');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post(`/members/${member.id}/make-hof`, formData);
      const data = response.data;
      toast.success(
        <div>
          <p className="font-semibold">New family created!</p>
          <p className="text-sm">HOF: {data.hof_name}</p>
          <p className="text-sm">ITS: {data.hof_its}</p>
          <p className="text-sm">{data.mohalla} - {data.sector}</p>
        </div>,
        { duration: 5000 }
      );
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create family');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link className="w-5 h-5 text-blue-600" />
            Tag to Different Family
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              Member: <span className="font-semibold">{member.full_name}</span>
            </p>
            <p className="text-xs text-blue-600 mt-1">
              Current Family: {member.hof_name || 'None'} | ITS: {member.its_id || 'N/A'}
            </p>
          </div>

          {/* Create as New HOF Checkbox */}
          <div className="flex items-center space-x-2 p-3 bg-slate-50 rounded-lg border">
            <input
              type="checkbox"
              id="createAsNewHof"
              checked={createAsNewHof}
              onChange={(e) => {
                setCreateAsNewHof(e.target.checked);
                setSelectedFamily(null);
                setSearchResults([]);
                setSearchIts('');
              }}
              className="w-4 h-4 text-emerald-600 border-slate-300 rounded focus:ring-emerald-500"
              data-testid="create-new-hof-checkbox"
            />
            <Label htmlFor="createAsNewHof" className="text-sm font-medium cursor-pointer">
              Create this as New HOF
            </Label>
          </div>

          {/* Search HOF by ITS - shown when NOT creating new HOF */}
          {!createAsNewHof && (
            <div className="space-y-3">
              <div>
                <Label className="form-label">Search HOF by ITS Number or Name</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Enter ITS number or HOF name..."
                    value={searchIts}
                    onChange={(e) => setSearchIts(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    data-testid="search-hof-its-input"
                  />
                  <Button 
                    onClick={handleSearch} 
                    disabled={searching}
                    variant="outline"
                    data-testid="search-hof-btn"
                  >
                    {searching ? '...' : <Search className="w-4 h-4" />}
                  </Button>
                </div>
              </div>

              {/* Search Results */}
              {searchResults.length > 0 && (
                <div className="border rounded-lg max-h-48 overflow-y-auto">
                  {searchResults.map(family => (
                    <div 
                      key={family.id}
                      onClick={() => setSelectedFamily(family)}
                      className={`p-3 cursor-pointer border-b last:border-b-0 hover:bg-slate-50 transition-colors ${
                        selectedFamily?.id === family.id ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                      }`}
                      data-testid={`family-option-${family.id}`}
                    >
                      <div className="font-medium text-sm">{family.hof_name}</div>
                      <div className="text-xs text-slate-500">
                        ITS: {family.hof_its_id || 'N/A'} | {family.mohalla} - {family.sector}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Selected Family Preview */}
              {selectedFamily && (
                <div className="space-y-3">
                  <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                    <p className="text-sm text-emerald-800 font-medium">Selected Family:</p>
                    <p className="text-sm text-emerald-700">{selectedFamily.hof_name}</p>
                    <p className="text-xs text-emerald-600">
                      ITS: {selectedFamily.hof_its_id || 'N/A'} | {selectedFamily.mohalla} - {selectedFamily.sector}
                    </p>
                  </div>
                  
                  {/* Relationship selector */}
                  <div>
                    <Label className="form-label">Relationship to new HOF</Label>
                    <Select value={selectedRelationship} onValueChange={setSelectedRelationship}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select relationship" />
                      </SelectTrigger>
                      <SelectContent>
                        {RELATIONSHIPS.filter(r => r !== 'HOF').map(r => (
                          <SelectItem key={r} value={r}>{r}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {isCurrentlyHof && (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                      <p className="font-medium">⚠️ Note: This member is currently HOF</p>
                      <p className="text-xs mt-1">Moving will convert them to a family member and may affect their original family.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Mohalla & Sector Selection - Only shown when checkbox is ticked */}
          {createAsNewHof && (
            <div className="space-y-4 animate-fade-in">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="form-label">Mohalla *</Label>
                  <Select 
                    value={formData.mohalla} 
                    onValueChange={(v) => setFormData({...formData, mohalla: v, sector: ''})}
                  >
                    <SelectTrigger data-testid="new-hof-mohalla-select">
                      <SelectValue placeholder="Select Mohalla" />
                    </SelectTrigger>
                    <SelectContent>
                      {mohallas.map(m => (
                        <SelectItem key={m.id || m.name} value={m.name}>{m.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="form-label">Sector *</Label>
                  <Select 
                    value={formData.sector} 
                    onValueChange={(v) => setFormData({...formData, sector: v})}
                    disabled={!formData.mohalla}
                  >
                    <SelectTrigger data-testid="new-hof-sector-select">
                      <SelectValue placeholder="Select Sector" />
                    </SelectTrigger>
                    <SelectContent>
                      {sectors.map(s => (
                        <SelectItem key={s.id || s.name} value={s.name}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label className="form-label">Thaali Number (Optional)</Label>
                <Input
                  value={formData.thaali_number}
                  onChange={(e) => setFormData({...formData, thaali_number: e.target.value})}
                  placeholder="e.g., 123"
                  data-testid="new-hof-thaali-input"
                />
              </div>

              <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-800">
                <p className="font-medium">What will happen:</p>
                <ul className="list-disc list-inside mt-1 text-xs">
                  <li>Member will be removed from current family</li>
                  <li>A new family will be created with this member as HOF</li>
                </ul>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
          {createAsNewHof ? (
            <Button 
              onClick={handleCreateNewHof} 
              disabled={loading || !formData.mohalla || !formData.sector}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="create-new-hof-btn"
            >
              {loading ? 'Creating...' : 'Create New Family'}
            </Button>
          ) : (
            <Button 
              onClick={handleTagToFamily} 
              disabled={loading || !selectedFamily}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="tag-to-family-btn"
            >
              {loading ? 'Moving...' : 'Tag to Family'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MakeHofDialog({ member, api, onClose, onSuccess }) {
  const [mohallas, setMohallas] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [formData, setFormData] = useState({
    mohalla: '',
    sector: '',
    thaali_number: '',
    address: '',
    flat_number: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchMohallas();
  }, []);

  useEffect(() => {
    if (formData.mohalla) {
      fetchSectors(formData.mohalla);
    }
  }, [formData.mohalla]);

  const fetchMohallas = async () => {
    try {
      const response = await api.get('/mohallas');
      setMohallas(response.data.mohallas || []);
    } catch (error) {
      console.error('Failed to fetch mohallas');
    }
  };

  const fetchSectors = async (mohallaName) => {
    try {
      const response = await api.get(`/sectors?mohalla=${encodeURIComponent(mohallaName)}`);
      setSectors(response.data.sectors || []);
    } catch (error) {
      console.error('Failed to fetch sectors');
    }
  };

  const handleMakeHof = async () => {
    if (!formData.mohalla || !formData.sector) {
      toast.error('Please select Mohalla and Sector');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post(`/members/${member.id}/make-hof`, formData);
      const data = response.data;
      toast.success(
        <div>
          <p className="font-semibold">New family created!</p>
          <p className="text-sm">HOF: {data.hof_name}</p>
          <p className="text-sm">ITS: {data.hof_its}</p>
          <p className="text-sm">{data.mohalla} - {data.sector}</p>
        </div>,
        { duration: 5000 }
      );
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create family');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Crown className="w-5 h-5 text-gold" />
            Make HOF (Create New Family)
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
            <p className="text-sm text-emerald-800">
              New HOF: <span className="font-semibold">{member.full_name}</span>
            </p>
            <p className="text-xs text-emerald-600 mt-1">
              ITS: {member.its_id || 'N/A'}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="form-label">Mohalla *</Label>
              <Select 
                value={formData.mohalla} 
                onValueChange={(v) => setFormData({...formData, mohalla: v, sector: ''})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Mohalla" />
                </SelectTrigger>
                <SelectContent>
                  {mohallas.map(m => (
                    <SelectItem key={m.id || m.name} value={m.name}>{m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="form-label">Sector *</Label>
              <Select 
                value={formData.sector} 
                onValueChange={(v) => setFormData({...formData, sector: v})}
                disabled={!formData.mohalla}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Sector" />
                </SelectTrigger>
                <SelectContent>
                  {sectors.map(s => (
                    <SelectItem key={s.id || s.name} value={s.name}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="form-label">Thaali Number (Optional)</Label>
            <Input
              value={formData.thaali_number}
              onChange={(e) => setFormData({...formData, thaali_number: e.target.value})}
              placeholder="e.g., 123"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="form-label">Address / Building</Label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({...formData, address: e.target.value})}
                placeholder="Building name"
              />
            </div>
            <div>
              <Label className="form-label">Flat Number</Label>
              <Input
                value={formData.flat_number}
                onChange={(e) => setFormData({...formData, flat_number: e.target.value})}
                placeholder="e.g., A-101"
              />
            </div>
          </div>

          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
            <p className="font-medium">What will happen:</p>
            <ul className="list-disc list-inside mt-1 text-xs">
              <li>Member will be removed from current family</li>
              <li>A new family will be created with this member as HOF</li>
              <li>Member&apos;s relationship will change to &quot;HOF&quot;</li>
              <li>This action is logged for audit</li>
            </ul>
          </div>
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
          <Button 
            onClick={handleMakeHof} 
            disabled={loading || !formData.mohalla || !formData.sector}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            {loading ? 'Creating...' : 'Create New Family'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
// Dialog to assign Mohalla/Sector to untagged HOF families
function AssignMohallaDialog({ member, api, onClose, onSuccess }) {
  const [mohallas, setMohallas] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [formData, setFormData] = useState({ mohalla: '', sector: '' });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchMohallas = async () => {
      try {
        const response = await api.get('/mohallas');
        setMohallas(response.data.mohallas || []);
      } catch (error) {
        console.error('Failed to fetch mohallas');
      }
    };
    fetchMohallas();
  }, [api]);

  useEffect(() => {
    if (formData.mohalla) {
      const fetchSectors = async () => {
        try {
          const response = await api.get(`/sectors?mohalla=${encodeURIComponent(formData.mohalla)}`);
          setSectors(response.data.sectors || []);
        } catch (error) {
          console.error('Failed to fetch sectors');
        }
      };
      fetchSectors();
    } else {
      setSectors([]);
    }
  }, [formData.mohalla, api]);

  const handleAssign = async () => {
    if (!formData.mohalla || !formData.sector) {
      toast.error('Please select both Mohalla and Sector');
      return;
    }

    setLoading(true);
    try {
      // Get the family ID from the member
      const familyId = member.hof_id || `HOF-${member.its_id}`;
      
      // Update the family with Mohalla and Sector
      await api.put(`/families/${familyId}`, {
        mohalla: formData.mohalla,
        sector: formData.sector
      });
      
      toast.success(`Assigned ${member.full_name} to ${formData.mohalla} - ${formData.sector}`);
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign Mohalla/Sector');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="w-5 h-5 text-blue-600" />
            Assign Mohalla & Sector
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              HOF: <span className="font-semibold">{member.full_name}</span>
            </p>
            <p className="text-xs text-blue-600 mt-1">
              ITS: {member.its_id || 'N/A'}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="form-label">Mohalla *</Label>
              <Select 
                value={formData.mohalla} 
                onValueChange={(v) => setFormData({...formData, mohalla: v, sector: ''})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Mohalla" />
                </SelectTrigger>
                <SelectContent>
                  {mohallas.map(m => (
                    <SelectItem key={m.id || m.name} value={m.name}>{m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="form-label">Sector *</Label>
              <Select 
                value={formData.sector} 
                onValueChange={(v) => setFormData({...formData, sector: v})}
                disabled={!formData.mohalla}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Sector" />
                </SelectTrigger>
                <SelectContent>
                  {sectors.map(s => (
                    <SelectItem key={s.id || s.name} value={s.name}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
          <Button 
            onClick={handleAssign} 
            disabled={loading || !formData.mohalla || !formData.sector}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {loading ? 'Assigning...' : 'Assign'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

